import { useCallback, useEffect, useState } from "react";
import { foodApi, getToken } from "./api";

const QUEUE_KEY = "kt_order_queue";
const CACHE_KEY = "kt_orders_cache";
const listeners = new Set();

const read = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } };
const write = (q) => { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); listeners.forEach((fn) => fn(q)); };

export const isNetworkError = (e) => !e?.response;
export const getQueue = read;
export const pendingStatus = (orderId) => { const q = read().filter((i) => i.order_id === orderId); return q.length ? q[q.length - 1].status : null; };

export const cacheOrders = (orders) => { if (getToken()) localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), orders })); };
export const readCachedOrders = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; } };

export const enqueue = (orderId, status) => {
  const q = read().filter((i) => i.order_id !== orderId || !isSameLane(i.status, status));
  q.push({ id: `${orderId}:${status}:${Date.now()}`, order_id: orderId, status, at: new Date().toISOString() });
  write(q);
};
// A later "cancelled" replaces queued advances for the same order; advances just append in sequence.
const isSameLane = (a, b) => b === "cancelled";

// Offline-aware status change: returns the (optimistic) order to render.
export async function setOrderStatus(order, status, extra) {
  if (!navigator.onLine) { enqueue(order.order_id, status); return { ...order, status, _pending: true }; }
  try {
    return await foodApi.orderStatus(order.order_id, status, undefined, undefined, extra);
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    enqueue(order.order_id, status);
    return { ...order, status, _pending: true };
  }
}

let flushing = false;
export async function flush({ onSynced, onConflict } = {}) {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    let q = read();
    while (q.length) {
      const item = q[0];
      try {
        const updated = await foodApi.orderStatus(item.order_id, item.status);
        onSynced?.(updated, item);
      } catch (e) {
        if (isNetworkError(e)) return; // still offline; keep the queue intact
        onConflict?.(item, e);
      }
      q = q.slice(1);
      write(q);
    }
  } finally { flushing = false; }
}

export function useOfflineQueue({ onSynced, onConflict } = {}) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [queue, setQueue] = useState(read);
  const doFlush = useCallback(() => flush({ onSynced, onConflict }), [onSynced, onConflict]);
  useEffect(() => { listeners.add(setQueue); return () => listeners.delete(setQueue); }, []);
  useEffect(() => {
    const up = () => { setOnline(true); doFlush(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    if (navigator.onLine) doFlush();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, [doFlush]);
  return { online, queue, flush: doFlush };
}
